import * as React from "react";
import { Switch, Text, Divider } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { settingsService } from "../../../../services";
import { INotificationSettings } from "../../../../services/types/settings";
import { AppButton, AppLoader } from "../../../../components/common";
import useStyles from "./SettingsStyles";

const NotificationSettingsView: React.FC = () => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [settingsResponse, setSettingsResponse] = useState<INotificationSettings>();
  const [settingSwitches, setSettingSwitches] = useState([
    {
      id: "all-notifications",
      label: "Wszystkie powiadomienia",
      value: true,
    },
    {
      id: "worktime",
      label: "Wszystkie powiadomienia z sekcji czas pracy",
      value: true,
    },
    {
      id: "worktime-start-notification",
      label: "Powiadomienie o rozpoczęciu pracy",
      value: true,
    },
    {
      id: "worktime-end-notification",
      label: "Powiadomienie o zakończeniu pracy",
      value: true,
    },
    {
      id: "worktime-modification-notification",
      label: "Powiadomienie o modyfikacji czasu pracy przez administratora",
      value: true,
    },
    {
      id: "assignment",
      label: "Wszystkie powiadomienia z sekcji zlecenia",
      value: true,
    },
    {
      id: "assignment-new",
      label: "Powiadomienie o nowym zleceniu wykonania nadgodzin",
      value: true,
    },
    {
      id: "assignment-canceled",
      label: "Powiadomienie o anulowaniu zlecenia",
      value: true,
    },
    {
      id: "assignment-rejected",
      label: "Powiadomienie o odrzuceniu zlecenia",
      value: true,
    },
    {
      id: "assignment-realized",
      label: "Powiadomienie o zrealizowaniu zlecenia nadgodzin",
      value: true,
    },
    {
      id: "assignment-not-realized", 
      label: "Powiadomienie o niezrealizowaniu zlecenia nadgodzin",
      value: true,
    },
    {
      id: "assignment-accepted",
      label: "Powiadomienie o zaakceptowaniu zlecenia nadgodzin", 
      value: true,
    },
    {
      id: "application",
      label: "Wszystkie powiadomienia z sekcji wnioski",
      value: true,
    },
    {
      id: "applications-canceled",
      label: "Powiadomienie o anulowaniu wniosku",
      value: true,
    },
    {
      id: "applications-accepted-overtime",
      label: "Powiadomienie o zaakceptowaniu wniosku o nadgodziny",
      value: true,
    },
    {
      id: "applications-accepted-leaving",
      label: "Powiadomienie o zaakceptowaniu wniosku o wyjście",
      value: true,
    },
    {
      id: "applications-rejected",
      label: "Powiadomienie o odrzuceniu wniosku nadgodzin",
      value: true,
    },
    {
      id: "applications-new",
      label: "Powiadomienie o nowym wniosku",
      value: true,
    },
    {
      id: "time-collection",
      label: "Wszystkie powiadomienia z sekcji zbieranie czasu",
      value: true,
    },
    {
      id: "time-collection-collected-time",
      label: "Powiadomienie o odebraniu nadgodzin",
      value: true,
    },
    {
      id: "time-collection-deleted",
      label: "Powiadomienie o usunięciu wpisu odebrania nadgodzin",
      value: true,
    },
    {
      id: "users",
      label: "Wszystkie powiadomienia z sekcji użytkownicy",
      value: true,
    },
    {
      id: "users-new",
      label: "Powiadomienie o dołączeniu do aplikacji Overtimer",
      value: true,
    }
  ]);

  const [groupLabels, setGroupLabels] = useState<{ id: string; label: string }[]>([
    {
      id: "worktime",
      label: "SEKCJA: Czas pracy",
    },
    {
      id: "assignment",
      label: "SEKCJA: Zlecenia",
    },
    {
      id: "application",
      label: "SEKCJA: Wnioski",
    },
    {
      id: "time-collection",
      label: "SEKCJA: Zbieranie czasu",
    },
    {
      id: "users",
      label: "SEKCJA: Użytkownicy",
    },
  ]);

  const getGroupLabel = (id: string): JSX.Element => {
    const group = groupLabels.find((element) => element.id === id);

    if (group) {
      return (
        <>
          <div className={styles.groupHeader}>
            <h3>{group.label}</h3>
            <Divider className={styles.divider} />
          </div>
        </>
      );
    }

    return <></>;
  };

  const fetchSettings = async () => {
    setIsLoading(true);

    const response: INotificationSettings = await settingsService.getNotificationSettings();

    setSettingsResponse(response);
    setSettingSwitches((prev) => {
      prev[2].value = response.notifyWorktimeStart;
      prev[3].value = response.notifyWorktimeEnd;
      prev[4].value = response.notifyWorktimeModification;
      prev[6].value = response.notifyNewAssignnment;
      prev[7].value = response.notifyAssignmentCanceled;
      prev[8].value = response.notifyAssignmentRejected;
      prev[9].value = response.notifyAssignmentRealized;
      prev[10].value = response.notifyAssignmentNotRealized;
      prev[11].value = response.notifyAssignmentAcceptedOvertime;
      prev[13].value = response.notifyApplicationCanceled;
      prev[14].value = response.notifyApplicationAcceptedOvertime;
      prev[15].value = response.notifyApplicationAcceptedLeaving;
      prev[16].value = response.notifyApplicationRejected;
      prev[17].value = response.notifyNewApplication;
      prev[19].value = response.notifyCollectedTime;
      prev[20].value = response.notifyDeletedTimeCollection;
      prev[22].value = response.notifyNewUser;

      return prev;
    });

    updateMajorSwitches();
    setIsLoading(false);
  };

  const determineEditStateOnChange = () => {
    if (settingsResponse) {
      const isEdited = settingSwitches.some((setting) => {
        const notificationSettingsMap = {
          'worktime-start-notification': 'notifyWorktimeStart',
          'worktime-end-notification': 'notifyWorktimeEnd',
          'worktime-modification-notification': 'notifyWorktimeModification',
          'assignment-new': 'notifyNewAssignnment',
          'assignment-canceled': 'notifyAssignmentCanceled',
          'assignment-rejected': 'notifyAssignmentRejected',
          'assignment-realized': 'notifyAssignmentRealized',
          'assignment-not-realized': 'notifyAssignmentNotRealized',
          'assignment-accepted': 'notifyAssignmentAcceptedOvertime',
          'applications-canceled': 'notifyApplicationCanceled',
          'applications-accepted-overtime': 'notifyApplicationAcceptedOvertime',
          'applications-accepted-leaving': 'notifyApplicationAcceptedLeaving',
          'applications-rejected': 'notifyApplicationRejected',
          'applications-new': 'notifyNewApplication',
          'time-collection-collected-time': 'notifyCollectedTime',
          'time-collection-deleted': 'notifyDeletedTimeCollection',
          'users-new': 'notifyNewUser',
        } as const;
        
        const responseValue = settingsResponse[notificationSettingsMap[setting.id as keyof typeof notificationSettingsMap]] ?? false;
        return setting.value !== responseValue;
      });

      setIsEdited(isEdited);
    }
  };

  const handleSettingsChange = (id: string) => {
    setSettingSwitches((prevSettings) => {
      const updatedSettings = prevSettings.map((setting) => {
        switch (id) {
          case "all-notifications":
            return { ...setting, value: !prevSettings[0].value };

          case "worktime":
            if (setting.id.startsWith("worktime")) {
              const worktimeGroupValue = prevSettings.find(
                (s) => s.id === "worktime"
              )?.value;
              return { ...setting, value: !worktimeGroupValue };
            }
            break;

          case "assignment":
            if (setting.id.startsWith("assignment")) {
              const assignmentsGroupValue = prevSettings.find(
                (s) => s.id === "assignment"
              )?.value;
              return { ...setting, value: !assignmentsGroupValue };
            }
            break;

          case "application":
            if (setting.id.startsWith("application")) {
              const applicationsGroupValue = prevSettings.find(
                (s) => s.id === "application"
              )?.value;
              return { ...setting, value: !applicationsGroupValue };
            }
            break;

          case "time-collection":
            if (setting.id.startsWith("time-collection")) {
              const timeCollectionsGroupValue = prevSettings.find(
                (s) => s.id === "time-collection"
              )?.value;
              return { ...setting, value: !timeCollectionsGroupValue };
            }
            break;

          case "users":
            if (setting.id.startsWith("users")) {
              const usersGroupValue = prevSettings.find(
                (s) => s.id === "users"
              )?.value;
              return { ...setting, value: !usersGroupValue };
            }
            break

          default:
            if (setting.id === id) {
              return { ...setting, value: !setting.value };
            }
        }
        return setting;
      });

      return updatedSettings;
    });

    updateMajorSwitches();
  };

  const updateMajorSwitches = () => {
    setSettingSwitches((prevSettings) => {

      const groupHasAnyFalse = (matchPhrase: string, ignore: string) => prevSettings
          .filter((s) => s.id.startsWith(matchPhrase) && s.id !== ignore)
          .some((s) => !s.value);
      
      prevSettings = prevSettings.map((setting) => {
        switch (setting.id) {
          case "worktime":
            return {...setting, value: !groupHasAnyFalse("worktime", "worktime")};

          case "assignment":
            return {...setting, value: !groupHasAnyFalse("assignment", "assignment")};

          case "application":
            return {...setting, value: !groupHasAnyFalse("application", "application")};

          case "time-collection":
            return {...setting, value: !groupHasAnyFalse("time-collection", "time-collection")};

          case "users":
            return {...setting, value: !groupHasAnyFalse("users", "users")};

          default:
            return setting;
        }
      });

      return prevSettings.map(setting => 
          setting.id === "all-notifications" 
        ? {...setting, value: !groupHasAnyFalse("", "all-notifications")} 
        : setting
      );
    });
  };

  const handleSave = async () => {
    setIsSaving(true);

    let updatedSettings = {
      notifyWorktimeStart: settingSwitches[2].value,
      notifyWorktimeEnd: settingSwitches[3].value,
      notifyWorktimeModification: settingSwitches[4].value,
      notifyNewAssignnment: settingSwitches[6].value,
      notifyAssignmentCanceled: settingSwitches[7].value,
      notifyAssignmentRejected: settingSwitches[8].value,
      notifyAssignmentRealized: settingSwitches[9].value,
      notifyAssignmentNotRealized: settingSwitches[10].value,
      notifyAssignmentAcceptedOvertime: settingSwitches[11].value,
      notifyApplicationCanceled: settingSwitches[13].value,
      notifyApplicationAcceptedOvertime: settingSwitches[14].value,
      notifyApplicationAcceptedLeaving: settingSwitches[15].value,
      notifyApplicationRejected: settingSwitches[16].value,
      notifyNewApplication: settingSwitches[17].value,
      notifyCollectedTime: settingSwitches[19].value,
      notifyDeletedTimeCollection: settingSwitches[20].value,
      notifyNewUser: settingSwitches[22].value,
    }

    await settingsService.updateSettings(updatedSettings);
    setSettingsResponse(updatedSettings);

    setIsSaving(false);
    setIsEdited(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    determineEditStateOnChange();
  }, [settingSwitches]);

  return (
    <div className={styles.root}>
      {isLoading ? (
        <div className={`${styles.loaderHeight}`}>
          <AppLoader />
        </div>
      ) : (
        <>
          <Text size={600} weight="semibold" className={styles.title}>
            Konfiguracja powiadomień
          </Text>
          <div className={styles.section}>
            <div className={styles.switchGroup}>
              {settingSwitches.map((setting) => (
                <>
                  {getGroupLabel(setting.id)}
                  <span className={styles.spanStyle}>
                    {setting.label}
                    <Switch
                      key={setting.id}
                      checked={setting.value}
                      disabled={isSaving}
                      onChange={() => handleSettingsChange(setting.id)}
                    />
                  </span>
                </>
              ))}
            </div>
          </div>
          {isEdited && (
            <div className={styles.buttonContainer}>
              <AppButton
                onClick={handleSave}
                disabled={isSaving}
                text="Zapisz zmiany"
              ></AppButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationSettingsView;